import { useEffect, useState } from 'react';
import PrimaryButton from '~/src/components/common/forms/Button/PrimaryButton';
import TitleComponent from '~/src/components/common/forms/Title/TitleComponent';
import WhitePad from '~/src/components/common/WhitePad/WhitePad';
import NutritionLabelAndSearch from '../components/NutritionLabelAndSearch';
import CombinationsTable from '../components/CombinationsTable';
import { useHistory, useParams } from 'react-router-dom';
import { StatusCodes } from 'http-status-codes';
import IngredientsApi from '~/src/services/api/IngredientsApi';
import { IngredientGetDto } from '~/src/types/ingredientGetDto';
import IngredientBox from '../components/IngredientBox';
import _ from 'lodash';

interface ParamProps {
  productId: string;
  ingredientId: string;
}

export default function NutritionHomepage(): JSX.Element {
  const { productId, ingredientId } = useParams<ParamProps>();
  const [parentIngredient, setParentIngredient] = useState<IngredientGetDto | null>(null);
  const [comboStatus, setComboStatus] = useState('');
  const [calculate202, setCalculate202] = useState<string>('');
  const [calculateLoading, setCalculateLoading] = useState<boolean>(false);
  const [statusLoading, setStatusLoading] = useState<boolean>(false);
  const history = useHistory();
  
  useEffect(() => {
    if (productId) {
      getParentAndChildIngredients();
    }
    if (ingredientId) {
      getIngredient2();
    }
  }, []);

  const calculatePercentages = async (): void => {
    if (parentIngredient) {
      setCalculateLoading(true);
      const res = await IngredientsApi.calculateRemotely(parentIngredient);

      if (res.status === StatusCodes.OK || res.status === StatusCodes.ACCEPTED) {
        setCalculate202('Data accepted for crunching');

        // Reload ingredient to get updated errorScore and percentFromWhole
        const updated = await IngredientsApi.getIngredient(parentIngredient.id);
        if (updated.status === StatusCodes.OK) {
          const ordered = _.orderBy(updated.data?.childIngredients, ['order'], ['asc']);
          updated.data.childIngredients = ordered;
          setParentIngredient(updated.data);
          setCalculate202('Calculation complete');
        }

        setTimeout(() => {
          setCalculate202('');
        }, 4000);
      }
      setCalculateLoading(false);
    }
  };

  const getParentAndChildIngredients = async (): Promise<void> => {
    const res = await IngredientsApi.getOrCreateForProduct(productId);

    if (res.status === StatusCodes.OK) {
      const ordered = _.orderBy(res.data?.childIngredients, ['order'], ['asc']);
      res.data.childIngredients = ordered;
      setParentIngredient(res.data);
    }
  };

  const getIngredient2 = async (): Promise<void> => {
    const res = await IngredientsApi.getIngredient(ingredientId);

    if (res.status === StatusCodes.OK) {
      setParentIngredient(res.data);
    }
  };

  const updateChild = (child: IngredientGetDto): void => {
    const copiedIngredient = _.cloneDeep(parentIngredient);
    const end = copiedIngredient?.childIngredients?.length ?? 0;
    
    for (let i = 0; i < end; i++) {
      if (copiedIngredient?.childIngredients[i].id === child.id) {
        copiedIngredient.childIngredients[i] = child;
        break;
      }
    }

    setParentIngredient(copiedIngredient);
  };

  const getCalcStatus = async () => {
    setStatusLoading(true);
    setComboStatus('Loading results status');
    const res = await IngredientsApi.getRemotely(parentIngredient.id);
    const status = res.data.status;
    setComboStatus(status);

    if (status === 'SAVED_TO_SERVER') {
      const updated = await IngredientsApi.getIngredient(parentIngredient.id);
      if (updated.status === StatusCodes.OK) {
        const ordered = _.orderBy(updated.data?.childIngredients, ['order'], ['asc']);
        updated.data.childIngredients = ordered;
        setParentIngredient(updated.data);
      }
    }

    setTimeout(() => {
      setComboStatus('');
    }, 4000);
    setStatusLoading(false);
  };

  return (
    <WhitePad isFluid>
      <TitleComponent
       title="Nutrition" 
      titleLeft="Back"
      onLeftHandler={(): void => history.goBack()}
      />
      {parentIngredient?.name}
      {parentIngredient && (
        <NutritionLabelAndSearch isParent ingredient={parentIngredient} setIngredient={setParentIngredient} />
      )}

      {parentIngredient?.childIngredients?.map((ingredient) => (
        <IngredientBox ingredient={ingredient} key={ingredient.id} setIngredient={updateChild} />
      ))}

      {parentIngredient?.nutritionSet && parentIngredient?.childIngredients && (
        <>
          <div>
            <PrimaryButton
              variant="primary"
              title="Calculate"
              type="button"
              onClick={calculatePercentages}
              size="sm"
              isSubmitting={calculateLoading}
            />
            {calculate202}
          </div>
          <div>
            <PrimaryButton title="Refresh status" onClick={getCalcStatus} size="sm" isSubmitting={statusLoading} />
            {comboStatus}
          </div>
        </>
      )}
      {parentIngredient && <CombinationsTable target={parentIngredient} />}
    </WhitePad>
  );
}
